import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { type NavigateOptions, useLocation, useNavigate } from 'react-router';

import { InstantSearchContext } from './instantSearchContext';
import { InstantSearchSortOrder, type QueryState } from './types';
import {
  getQueryStateFromURLSearchParams,
  getURLSearchParamsFromQueryState,
  isQueryStateEqual,
} from './util/search-util';
import { getSafeURLSearchParams } from './util/url-search-params';

type CommonProps = {
  defaultQueryState?: Partial<QueryState>;
  ignoredFields?: (keyof QueryState)[];
  onQueryStateChange?: (newQueryState: QueryState) => void;
};

type Props =
  | (CommonProps & {
      urlSync?: false;
      prefix?: never;
    })
  | (CommonProps & {
      urlSync?: true;
      /**
       * A prefix to add to each url parameter key. This is used to distinguish query params managed by
       * this InstantSearch instance. It is also helpful for when you have multiple instances of
       * InstantSearch on the same page and want to avoid conflicts between the query params.
       */
      prefix: string;
    })
  | (CommonProps & {
      urlSync?: boolean;
      prefix?: string;
    });

const InstantSearch = ({
  defaultQueryState = {},
  ignoredFields = [],
  prefix,
  urlSync = true,
  onQueryStateChange,
  children,
}: PropsWithChildren<Props>) => {
  const { pathname, search } = useLocation();
  const initialQueryState: QueryState = useMemo(
    () => ({
      page: 1,
      size: 20,
      sortOrder: InstantSearchSortOrder.ASC,
      sortBy: '',
      search: '',
      filters: [],
      ...defaultQueryState,
    }),
    [defaultQueryState],
  );
  const navigate = useNavigate();
  const [queryState, setQueryState] = useState<QueryState>(() => {
    if (urlSync) {
      return getQueryStateFromURLSearchParams(
        getSafeURLSearchParams(window.location.search),
        prefix!,
        initialQueryState,
      );
    } else {
      return initialQueryState;
    }
  });
  const queryStateRef = useRef(queryState);
  const pathnameRef = useRef(pathname);

  const hasManagedSearchParams = (searchParams: URLSearchParams) => {
    return Array.from(searchParams.keys()).some((key) => key.startsWith(`${prefix!}.`));
  };

  const handleQueryStateChange = (updatedQueryState: QueryState, options: NavigateOptions) => {
    // Only replace the search params if they are out of sync with the query state. If the user
    // performs an action that changes the query state, push the new query state to the URL.
    if (urlSync) {
      updateSearchParams(
        getURLSearchParamsFromQueryState(updatedQueryState, prefix!, ignoredFields),
        options,
      );
    }

    if (!isQueryStateEqual(queryStateRef.current, updatedQueryState)) {
      queryStateRef.current = updatedQueryState;
      setQueryState(updatedQueryState);
    }
  };

  const updateSearchParams = (
    updatedSearchParams: URLSearchParams,
    options: NavigateOptions = {},
  ) => {
    // Avoid stale-route writes from an InstantSearch instance that is being unmounted after
    // navigation. In that case, the browser pathname has already changed away from this component.
    const hasWindowPathname = typeof window.location.pathname === 'string';
    if (hasWindowPathname && window.location.pathname !== pathname) {
      return;
    }

    const searchParamsCopy = getSafeURLSearchParams(window.location.search);

    // Update the search params with the new values.
    updatedSearchParams.forEach((value, key) => {
      searchParamsCopy.set(key, value);
    });

    // Write all the search params back to the url.
    const paramsString = Array.from(searchParamsCopy.entries()).reduce((memo, [key, value]) => {
      // Only write the search params that are not managed by this InstantSearch instance, or
      // have been updated.
      if (!key.startsWith(`${prefix}.`) || updatedSearchParams.has(key)) {
        return `${memo}${memo.length > 0 ? '&' : '?'}${key}=${value}`;
      }
      return memo;
    }, '');

    if (searchParamsCopy.toString() !== window.location.search.substring(1)) {
      navigate(paramsString, options);
    }
  };

  const updateQueryState = (newQueryState: Partial<QueryState>) => {
    handleQueryStateChange({ ...queryStateRef.current, ...newQueryState }, { replace: false });
  };

  // The query state changed due to user interaction
  useEffect(() => {
    onQueryStateChange?.(queryState);
  }, [queryState, onQueryStateChange]);

  // The page reloaded, or user edited the url params by hand
  useEffect(() => {
    if (urlSync) {
      const searchParams = getSafeURLSearchParams(window.location.search);
      const didPathnameChange = pathnameRef.current !== pathname;

      // Ignore route transitions where this InstantSearch prefix is absent on the new path.
      // This prevents an about-to-unmount page from pushing its own defaults onto the next route.
      if (didPathnameChange && !hasManagedSearchParams(searchParams)) {
        pathnameRef.current = pathname;
        if (!isQueryStateEqual(queryStateRef.current, initialQueryState)) {
          queryStateRef.current = initialQueryState;
          setQueryState(initialQueryState);
        }
        return;
      }

      const updatedQueryState = getQueryStateFromURLSearchParams(
        searchParams,
        prefix!,
        initialQueryState,
      );

      handleQueryStateChange(updatedQueryState, { replace: true });
      pathnameRef.current = pathname;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, window.location.search, initialQueryState, urlSync]);

  return (
    <InstantSearchContext.Provider value={{ queryState, updateQueryState }}>
      {children}
    </InstantSearchContext.Provider>
  );
};

export default InstantSearch;
